import type { GetPaymentsNewResponse } from '@vy/api-definition';
import { fromNullToUndefined, isNotNullOrUndefined, vySerialize, withDefault } from '@vy/utils';
import { PAYMENT_FILTERABLE_FIELDS } from '@vy/payments-types';
import { FILTER_OPERATORS } from '@vy/query-specifications-types';
import type { UseMutationOptions } from 'react-query';
import { useMutation } from 'react-query';
import { useFromDayParam } from '../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../hooks/search-params/use-to-day-param';
import { useTableSorting } from '../../../hooks/use-table-sorting';
import type { FilterLineType } from '../../../components/new-design/vyTableFilter';
import { useFiltersParam } from '../../../pages/payments/PaymentsPage/payments-page-hooks/use-filters-param';
import { useQueryInputParams } from '../../../pages/payments/PaymentsPage/payments-page-hooks/use-query-input-param';
import { getPaymentsNew } from '../../../api/payments-api';
import { queryKeys } from '../../../services/query-key/query-keys';

const PAGE = 0;
const PAGE_SIZE = 100000;

export function useNonPagedPaymentsFromParams(
  options?: UseMutationOptions<GetPaymentsNewResponse, unknown>,
) {
  const [search] = useQueryInputParams();
  const [filters] = useFiltersParam();
  const [fromDay] = useFromDayParam();
  const [toDay] = useToDayParam();
  const { currentSortModel } = useTableSorting();

  const dateFilters: FilterLineType[] = [];
  if (isNotNullOrUndefined(fromDay)) {
    dateFilters.push({
      id: 'date-filter-from',
      field: PAYMENT_FILTERABLE_FIELDS.PAYMENT_DATE,
      operator: FILTER_OPERATORS.GTE,
      value: fromDay.startOf('day').utc(true).toISOString(),
    });
  }
  if (isNotNullOrUndefined(toDay)) {
    dateFilters.push({
      id: 'date-filter-to',
      field: PAYMENT_FILTERABLE_FIELDS.PAYMENT_DATE,
      operator: FILTER_OPERATORS.LTE,
      value: toDay.endOf('day').utc(true).toISOString(),
    });
  }

  const filtersForApi = [...withDefault(filters, []), ...dateFilters].map((filter) => {
    const { id: _id, ...rest } = filter as typeof filter & { id?: string };
    return rest;
  });

  const payload = {
    search: fromNullToUndefined(search),
    paymentFilters: vySerialize(filtersForApi),
    page: PAGE,
    pageSize: PAGE_SIZE,
    sort: vySerialize(fromNullToUndefined(currentSortModel)),
  };

  return useMutation({
    mutationKey: queryKeys.paymentsNewAll(...Object.values(payload)),
    mutationFn: async () => {
      return getPaymentsNew(payload);
    },
    ...options,
  });
}

